import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

const SectionContainer = styled.div`
  background: white;
  border-radius: 0.75rem;
  border: 1px solid #01010110;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  overflow: hidden;
`;

const SectionHeader = styled.div`
  padding: 1.5rem 2rem;
  background: #f1f1f1;
  border-bottom: 1px solid #01010110;
`;

const SectionTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: #333333;
  margin: 0;
`;

const SectionContent = styled.div`
  padding: 1.5rem;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  font-size: 0.875rem;
  font-weight: 500;
  color: #333333;
  margin-bottom: 0.25rem;
`;

const Input = styled.input`
  padding: 0.5rem 0.75rem;
  border: 1px solid #01010120;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  background: #f9f9f9;
  color: #333333;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: #00a693;
    box-shadow: 0 0 0 3px rgba(0, 166, 147, 0.1);
    background: white;
  }
`;

const Select = styled.select`
  padding: 0.5rem 0.75rem;
  border: 1px solid #01010120;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  background: #f9f9f9;
  color: #333333;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: #00a693;
    box-shadow: 0 0 0 3px rgba(0, 166, 147, 0.1);
    background: white;
  }

  &:disabled {
    background: #f3f4f6;
    color: #6b7280;
    cursor: not-allowed;
  }
`;

const DisabledOverlay = styled.div`
  position: relative;
  
  &::after {
    content: 'Coming Soon';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.7);
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    border-radius: 0.5rem;
    z-index: 10;
  }
`;

const BasicInformationSection = ({
  livestreamName,
  selectedCourtId,
  selectedCamera,
  selectedCameraId,
  availableCameras,
  courts,
  onLivestreamNameChange,
  onCourtChange,
  onCameraChange,
  gameType,
  onGameTypeChange
}) => {
  return (
    <SectionContainer>
      <SectionHeader>
        <SectionTitle>Basic Information</SectionTitle>
      </SectionHeader>
      <SectionContent>
        <FormField>
          <Label>Name</Label>
          <Input
            type="text"
            value={livestreamName}
            onChange={(e) => onLivestreamNameChange(e.target.value)}
            placeholder="Enter livestream name"
            required
          />
        </FormField>

        <FormGrid>
          <FormField>
            <Label>Court</Label>
            <Select
              value={selectedCourtId}
              onChange={(e) => onCourtChange(e.target.value)}
              required
            >
              <option value="">-- Select Court --</option>
              {courts.map(court => (
                <option key={court.id} value={court.id}>{court.courtName}</option>
              ))}
            </Select>
          </FormField>

          <FormField>
            <Label>Camera</Label>
            <Select
              value={selectedCameraId}
              onChange={(e) => onCameraChange(e)}
              disabled={!selectedCourtId || availableCameras.length === 0}
              required
            >
              <option value="">-- Select Camera --</option>
              {availableCameras.map(camera => (
                <option key={camera.id} value={camera.id}>
                  {camera.cameraName} {camera.active ? '(Active)' : '(Inactive)'}
                </option>
              ))}
            </Select>
            {(!selectedCourtId || availableCameras.length === 0) && (
              <p className="mt-2 text-sm text-gray-500 bg-gray-50 p-2 rounded-lg border border-gray-200">
                No cameras available for this court.
              </p>
            )}
          </FormField>
        </FormGrid>

        <FormField>
          <Label>Game Type</Label>
          <DisabledOverlay>
            <Select
              value={gameType}
              onChange={(e) => onGameTypeChange(e.target.value)}
              disabled
            >
              <option value="">-- Select Game Type --</option>
              <option value="PICKLEBALL">Pickleball</option>
              <option value="SOCCER">Soccer</option>
            </Select>
          </DisabledOverlay>
        </FormField>
      </SectionContent>
    </SectionContainer>
  );
};

BasicInformationSection.propTypes = {
  livestreamName: PropTypes.string.isRequired,
  selectedCourtId: PropTypes.string.isRequired,
  selectedCamera: PropTypes.string,
  selectedCameraId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  availableCameras: PropTypes.array.isRequired,
  courts: PropTypes.array.isRequired,
  onLivestreamNameChange: PropTypes.func.isRequired,
  onCourtChange: PropTypes.func.isRequired,
  onCameraChange: PropTypes.func.isRequired,
  gameType: PropTypes.string.isRequired,
  onGameTypeChange: PropTypes.func.isRequired,
};

export default BasicInformationSection; 